import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from "@angular/core";
import { DashLangService } from "../../../../../modules/home/dash/service/dash.lang.service";
import { s25Resource } from "../../../../../util/s25.resource";
import { S25PipesModule } from "../../../../../pipes/s25.pipes.module";
import { HomeDashAvailableSpaceFindAvailabilityComponent } from "./find-availability/home.dash.available.space.find.availability.component";
import { HomeDashAvailableSpaceFindLocationComponent } from "./find-location/home.dash.available.space.find.location.component";
import { HomeDashCustomize, HomeDashPanelModel } from "../home.dash.panel.model";
import { HomeDashPanelHeadChrome } from "../head/home.dash.panel.head.state";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";

type AvailableSpaceLang = {
    states: Record<number, string>;
    style: { panel: string };
    text: Record<number, string>;
    divider: { style: string; text: string };
};

/** Panel chooser plus sub-flows: find a location for a time, or find availability for a space name. */
@TypeManagerDecorator("s25-ng-dash-panel-available-space")
@Component({
    selector: "s25-ng-dash-panel-available-space",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        S25PipesModule,
        HomeDashAvailableSpaceFindLocationComponent,
        HomeDashAvailableSpaceFindAvailabilityComponent,
    ],
    host: { class: "s25-ng-dash-panel-available-space panel-available-space" },
    template: `
        @if (vm(); as data) {
            @if (showAvailableSpaceBody()) {
                <div class="sectionBody dash-panel-body dash-panel-body--padded collapse-no-animation">
                    @if (viewState() === "chooser") {
                        <ul class="none-list">
                            @for (item of asChooserItems(data.panel.items); track item.itemTypeId; let isLast = $last) {
                                <li class="center">
                                    <div
                                        [class.cancelled]="item.isCancelled"
                                        [class.s25-object]="!item.isInactive"
                                        tabindex="0"
                                        (click)="selectFlow(item.itemTypeId)"
                                        (keydown.enter)="selectFlow(item.itemTypeId)"
                                    >
                                        <div [innerHTML]="data.context.text[item.itemTypeId] | safeHTML"></div>
                                    </div>
                                    @if (!isLast) {
                                        <div [attr.id]="data.context.divider.style" class="divider">
                                            <span>{{ data.context.divider.text }}</span>
                                        </div>
                                    }
                                </li>
                            }
                        </ul>
                    }
                    @if (viewState() === "find_location") {
                        <s25-ng-dash-available-space-find-location />
                    }
                    @if (viewState() === "find_availability") {
                        <s25-ng-dash-available-space-find-availability />
                    }
                </div>
            }
        }
    `,
})
export class HomeDashPanelAvailableSpaceComponent {
    readonly panel = input.required<HomeDashPanelModel>();
    readonly isToolbox = input(false);
    readonly customize = input<HomeDashCustomize | undefined>(undefined);
    /** Bumped by the panel orchestrator when `isCollapsed` mutates in place on the shared model. */
    readonly refreshTick = input(0);
    /** Reports head chrome (e.g. back arrow) to {@link HomeDashPanelComponent}; parent owns state. */
    readonly headChromeChange = output<HomeDashPanelHeadChrome>();
    /** Bumped by the orchestrator when the user clicks the head back control. */
    readonly headBackTick = input(0);

    private readonly availableSpaceLang = s25Resource(
        async () => (await DashLangService.getLang()).panel?.["available_space"] as AvailableSpaceLang,
    );
    /** Lang state id → available_space | find_location | find_time */
    private readonly activeFlow = signal<string>("available_space");

    readonly vm = computed(() => {
        this.refreshTick();
        const context = this.availableSpaceLang.value();
        const panel = this.panel();
        if (!context) {
            return undefined;
        }
        return { context, panel };
    });

    readonly showAvailableSpaceBody = computed(() => {
        this.refreshTick();
        return !this.isToolbox() && !this.panel().isCollapsed;
    });

    readonly viewState = computed(() => {
        const flow = this.activeFlow();
        if (flow === "find_location") {
            return "find_location" as const;
        }
        if (flow === "find_time") {
            return "find_availability" as const;
        }
        return "chooser" as const;
    });

    constructor() {
        effect(() => {
            this.activeFlow();
            this.syncHeadChrome();
        });
        effect(() => {
            const tick = this.headBackTick();
            if (!tick) {
                return;
            }
            queueMicrotask(() => this.resetToInitialFlow());
        });
    }

    asChooserItems(items: unknown): { itemTypeId: number; isCancelled?: boolean; isInactive?: boolean }[] {
        return (items ?? []) as { itemTypeId: number; isCancelled?: boolean; isInactive?: boolean }[];
    }

    selectFlow(itemTypeId: number) {
        const context = this.availableSpaceLang.value();
        if (!context) {
            return;
        }
        const states = context.states as Record<number | string, string | undefined>;
        const next = states[itemTypeId];
        if (next) {
            this.activeFlow.set(next);
        }
    }

    /** Legacy `state.refresh()` → `set(panel.typeId)` (4000 → chooser). */
    private resetToInitialFlow() {
        const context = this.availableSpaceLang.value();
        const panel = this.panel();
        if (!context) {
            return;
        }
        const states = context.states as Record<number | string, string | undefined>;
        const next = states[panel.typeId as number];
        this.activeFlow.set(next ?? "available_space");
    }

    private syncHeadChrome() {
        const inChooser = this.activeFlow() === "available_space";
        this.headChromeChange.emit({ showBack: !inChooser });
    }
}
